<template>
  <div class="exported-courses-page">
    <template v-if="isDetailPage">
      <div class="page-header">
        <div>
          <div class="breadcrumb">
            <button class="link-btn" @click="goBackToCourses">浏览课程</button>
            <span>/</span>
            <span>课程题目</span>
          </div>
          <h1>{{ currentCourse.name }}</h1>
        </div>
        <div class="header-actions">
          <button class="page-btn" @click="goBackToCourses">返回课程列表</button>
        </div>
      </div>

      <section class="questions-panel">
        <div class="panel-header">
          <div>
            <h2>题目内容</h2>
            <p v-if="!isLoadingQuestions && !questionsError" class="panel-subtitle">
              已解析 {{ questions.length }} 道题目，当前显示第 {{ questionRangeText }} 题
            </p>
          </div>
        </div>

        <div v-if="questionsError" class="error-message">
          {{ questionsError }}
        </div>

        <div v-else-if="isLoadingQuestions" class="loading-state">
          <div class="spinner"></div>
          <p>题目解析中...</p>
        </div>

        <div v-else-if="questions.length === 0" class="empty-state">
          未解析到题目数据
        </div>

        <div v-else class="question-list">
          <article v-for="question in paginatedQuestions" :key="question.id" class="question-card">
            <div class="question-top">
              <span class="question-index">#{{ question.id }}</span>
              <span v-if="question.questionType" class="question-tag">{{ question.questionType }}</span>
            </div>
            <h3 class="question-title">{{ question.title || '未识别题干' }}</h3>
            <p v-if="question.subType" class="question-extra">小题类型：{{ question.subType }}</p>
            <p v-if="question.catalog" class="question-extra">目录：{{ question.catalog }}</p>
            <div v-if="question.options.length" class="options-list">
              <div v-for="option in question.options" :key="option.key" class="option-item">
                <span class="option-key">{{ option.key }}</span>
                <span class="option-value">{{ option.value }}</span>
              </div>
            </div>
            <div v-if="question.answer || question.knowledge" class="question-footer">
              <span v-if="question.answer"><strong>答案：</strong>{{ question.answer }}</span>
              <span v-if="question.knowledge"><strong>知识点：</strong>{{ question.knowledge }}</span>
            </div>
            <p v-if="question.analysis" class="analysis-text">
              <strong>解析：</strong>{{ question.analysis }}
            </p>
          </article>
        </div>

        <div v-if="totalQuestionPages > 1" class="question-pagination">
          <button
            class="page-btn"
            :disabled="questionPage <= 1"
            @click="changeQuestionPage(questionPage - 1)"
          >
            上一页
          </button>
          <span class="page-info">第 {{ questionPage }} / {{ totalQuestionPages }} 页</span>
          <button
            class="page-btn"
            :disabled="questionPage >= totalQuestionPages"
            @click="changeQuestionPage(questionPage + 1)"
          >
            下一页
          </button>
        </div>
      </section>
    </template>

    <template v-else>
      <div class="page-header">
        <div>
          <h1>浏览课程</h1>
          <p>点击课程进入详情页，再查看课程中的题目内容。</p>
        </div>
        <button class="refresh-btn" :disabled="isLoadingCourses || isLoadingMoreCourses" @click="refreshCourses">
          {{ isLoadingCourses || isLoadingMoreCourses ? '刷新中...' : '刷新课程' }}
        </button>
      </div>

      <section class="courses-panel">
        <div class="panel-header">
          <h2>课程列表</h2>
          <span v-if="pagination.total_items" class="panel-meta">共 {{ pagination.total_items }} 门</span>
        </div>

        <div v-if="coursesError" class="error-message">
          {{ coursesError }}
        </div>

        <div v-else-if="isLoadingCourses" class="loading-state">
          <div class="spinner"></div>
          <p>课程加载中...</p>
        </div>

        <div v-else-if="courses.length === 0" class="empty-state">
          暂无课程数据
        </div>

        <div v-else>
          <div class="course-list">
            <button
              v-for="course in courses"
              :key="course.id"
              class="course-card"
              @click="openCourse(course)"
            >
              <img
                v-if="shouldShowCourseCover(course)"
                class="course-cover"
                :src="getCourseCover(course)"
                :alt="course.name"
                loading="lazy"
                @error="handleCourseCoverError(course.id)"
              >
              <div v-else class="course-cover course-cover-placeholder">
                <svg
                  class="course-cover-placeholder-icon"
                  viewBox="0 0 1024 1024"
                  version="1.1"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path d="M930.816 83.456H93.184C41.984 83.456 0 125.44 0 176.64v670.208c0 51.2 41.984 93.184 93.184 93.184h837.632c51.2 0 93.184-41.984 93.184-93.184V176.64c0-51.2-41.984-93.184-93.184-93.184zM93.184 151.552h837.632c13.824 0 25.088 11.264 25.088 25.088v487.424c-47.616-73.728-112.64-170.496-148.48-207.872-30.208-31.744-61.44-38.4-81.92-38.4-45.568 0-78.848 30.72-85.504 37.376-13.312 12.288-183.808 171.52-204.288 191.488-10.24-11.776-49.664-55.808-87.552-93.696-24.576-24.576-55.808-32.256-89.088-22.528-21.504 6.144-36.864 18.432-39.424 20.48h-0.512l-151.552 130.56V176.64c0.512-13.824 11.776-25.088 25.6-25.088z m837.632 720.896H93.184c-13.824 0-25.088-11.264-25.088-25.088v-73.728l195.072-169.984c2.56-1.536 9.216-6.144 16.384-8.192 8.704-2.048 14.336-0.512 20.992 5.632 39.936 39.936 82.944 88.576 87.552 93.696 4.608 5.632 19.968 21.504 43.52 23.04 12.8 1.024 31.744-2.048 50.688-20.48 9.216-9.216 151.04-141.824 205.312-192l0.512-0.512 1.024-1.024c5.12-5.12 20.992-17.408 37.376-17.408h0.512c10.752 0 21.504 5.632 32.256 17.408 40.448 42.496 149.504 199.68 197.632 272.896v70.656c-1.024 13.824-12.288 25.088-26.112 25.088z"></path>
                  <path d="M272.896 442.368c54.272 0 97.792-44.032 97.792-97.792 0-54.272-44.032-97.792-97.792-97.792-54.272 0-97.792 44.032-97.792 97.792-0.512 53.76 43.52 97.792 97.792 97.792z m0-128c16.384 0 30.208 13.312 30.208 30.208 0 16.384-13.312 30.208-30.208 30.208-16.384 0-30.208-13.312-30.208-30.208 0-16.896 13.312-30.208 30.208-30.208z"></path>
                </svg>
                <span class="course-cover-placeholder-text">暂无图片</span>
              </div>
              <div class="course-info">
                <h3>{{ course.name }}</h3>
              </div>
            </button>
          </div>

          <div v-if="loadMoreError" class="load-more-error">
            {{ loadMoreError }}
          </div>

          <div
            v-if="hasMoreCourses"
            ref="loadMoreTrigger"
            class="load-more-indicator"
          >
            <div v-if="isLoadingMoreCourses" class="spinner"></div>
            <p>{{ isLoadingMoreCourses ? '正在加载下一页课程...' : '滑动到底部自动加载下一页' }}</p>
          </div>

          <div v-else class="load-more-finished">
            已加载全部课程
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import * as cptable from 'xlsx/dist/cpexcel.full.mjs';
import { API_BASE_URL } from '../config/api';

if (typeof XLSX.set_cptable === 'function') {
  XLSX.set_cptable(cptable.default || cptable);
}

const API_ORIGIN = new URL(API_BASE_URL).origin;
const QUESTIONS_PER_PAGE = 100;

export default {
  name: 'ExportedCoursesPage',
  data() {
    return {
      courses: [],
      currentCourse: {
        id: '',
        name: '课程题目',
        cover: ''
      },
      failedCourseCovers: {},
      questions: [],
      questionPage: 1,
      isLoadingCourses: false,
      isLoadingMoreCourses: false,
      isLoadingQuestions: false,
      coursesError: null,
      loadMoreError: null,
      questionsError: null,
      pagination: {
        page: 1,
        per_page: 10,
        total_items: 0,
        total_pages: 1
      }
    };
  },
  computed: {
    isDetailPage() {
      return Boolean(this.$route.params.courseId);
    },
    hasMoreCourses() {
      return this.pagination.page < this.pagination.total_pages;
    },
    totalQuestionPages() {
      return Math.max(1, Math.ceil(this.questions.length / QUESTIONS_PER_PAGE));
    },
    paginatedQuestions() {
      const start = (this.questionPage - 1) * QUESTIONS_PER_PAGE;
      return this.questions.slice(start, start + QUESTIONS_PER_PAGE);
    },
    questionRangeText() {
      if (this.questions.length === 0) return '0-0';
      const start = (this.questionPage - 1) * QUESTIONS_PER_PAGE + 1;
      const end = Math.min(this.questionPage * QUESTIONS_PER_PAGE, this.questions.length);
      return `${start}-${end}`;
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler() {
        this.initializePage();
      }
    }
  },
  beforeUnmount() {
    this.disconnectLoadMoreObserver();
  },
  methods: {
    async initializePage() {
      const { courseId } = this.$route.params;
      this.disconnectLoadMoreObserver();
      if (courseId) {
        this.currentCourse = {
          id: String(courseId),
          name: this.$route.query.name || `课程 ${courseId}`,
          cover: this.$route.query.cover || ''
        };
        this.fetchMaskedQuestions(courseId);
        return;
      }

      this.questions = [];
      this.questionPage = 1;
      this.questionsError = null;
      const page = Math.max(1, Number(this.$route.query.page || 1) || 1);
      await this.fetchCoursesUntil(page);
    },

    getAuthHeaders() {
      const token = localStorage.getItem('token');
      return token ? { Authorization: `Bearer ${token}` } : {};
    },

    resetCourseList() {
      this.courses = [];
      this.failedCourseCovers = {};
      this.coursesError = null;
      this.loadMoreError = null;
      this.pagination = {
        ...this.pagination,
        page: 1,
        total_items: 0,
        total_pages: 1
      };
    },

    async fetchCoursesUntil(targetPage = 1) {
      this.resetCourseList();

      for (let page = 1; page <= targetPage; page += 1) {
        const success = await this.fetchCourses(page, { append: page > 1 });
        if (!success) break;
      }

      this.observeLoadMoreTrigger();
    },

    async fetchCourses(page = this.pagination.page, { append = false } = {}) {
      if (append) {
        if (this.isLoadingCourses || this.isLoadingMoreCourses) return false;
        this.isLoadingMoreCourses = true;
        this.loadMoreError = null;
      } else {
        if (this.isLoadingCourses) return false;
        this.isLoadingCourses = true;
        this.coursesError = null;
      }

      try {
        const params = new URLSearchParams({
          page: String(page),
          per_page: String(this.pagination.per_page)
        });
        const response = await fetch(`${API_BASE_URL}/exported-courses?${params}`, {
          headers: this.getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error('获取课程列表失败');
        }

        const data = await response.json();
        const items = Array.isArray(data.items) ? data.items : [];
        this.courses = append ? [...this.courses, ...items] : items;
        this.pagination = {
          ...this.pagination,
          ...(data.pagination || {}),
          page: (data.pagination && data.pagination.page) || page
        };
        return true;
      } catch (error) {
        console.error('获取课程列表失败:', error);
        if (append) {
          this.loadMoreError = error.message || '加载更多课程失败';
        } else {
          this.coursesError = error.message || '获取课程列表失败';
        }
        return false;
      } finally {
        if (append) {
          this.isLoadingMoreCourses = false;
        } else {
          this.isLoadingCourses = false;
        }

        this.observeLoadMoreTrigger();
      }
    },

    getCourseCover(course) {
      if (!course.cover) return '';
      return course.cover.startsWith('http') ? course.cover : `${API_ORIGIN}${course.cover}`;
    },

    shouldShowCourseCover(course) {
      return Boolean(course.cover) && !this.failedCourseCovers[course.id];
    },

    handleCourseCoverError(courseId) {
      this.failedCourseCovers = {
        ...this.failedCourseCovers,
        [courseId]: true
      };
    },

    observeLoadMoreTrigger() {
      if (this.isDetailPage || !this.hasMoreCourses) {
        this.disconnectLoadMoreObserver();
        return;
      }

      this.$nextTick(() => {
        const trigger = this.$refs.loadMoreTrigger;
        if (!trigger) return;

        if (!this.loadMoreObserver) {
          this.loadMoreObserver = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry && entry.isIntersecting) {
              this.loadNextPage();
            }
          }, {
            threshold: 0.1
          });
        }

        this.loadMoreObserver.disconnect();
        this.loadMoreObserver.observe(trigger);
      });
    },

    disconnectLoadMoreObserver() {
      if (this.loadMoreObserver) {
        this.loadMoreObserver.disconnect();
      }
    },

    async loadNextPage() {
      if (!this.hasMoreCourses || this.isLoadingCourses || this.isLoadingMoreCourses) return;
      await this.fetchCourses(this.pagination.page + 1, { append: true });
    },

    async refreshCourses() {
      if (this.isLoadingCourses || this.isLoadingMoreCourses) return;
      await this.fetchCoursesUntil(1);
    },

    openCourse(course) {
      this.$router.push({
        name: 'ExportedCourseDetail',
        params: { courseId: course.id },
        query: {
          name: course.name,
          cover: course.cover || '',
          page: String(this.pagination.page)
        }
      });
    },

    goBackToCourses() {
      this.$router.push({
        name: 'ExportedCourses',
        query: {
          page: String(this.$route.query.page || this.pagination.page || 1)
        }
      });
    },

    async fetchMaskedQuestions(courseId) {
      this.isLoadingQuestions = true;
      this.questionsError = null;
      this.questions = [];
      this.questionPage = 1;

      try {
        const response = await fetch(`${API_BASE_URL}/exported-courses/${courseId}/masked-file`, {
          headers: this.getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error('获取课程题目文件失败');
        }

        const buffer = await response.arrayBuffer();
        const workbook = XLSX.read(buffer, {
          type: 'array',
          codepage: 936,
          cellHTML: false
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          defval: '',
          blankrows: false
        });

        this.questions = this.normalizeQuestions(rows);
      } catch (error) {
        console.error('解析课程题目失败:', error);
        this.questionsError = error.message || '解析课程题目失败';
      } finally {
        this.isLoadingQuestions = false;
      }
    },

    changeQuestionPage(page) {
      if (page < 1 || page > this.totalQuestionPages || page === this.questionPage) return;
      this.questionPage = page;
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    },

    normalizeQuestions(rows) {
      if (!Array.isArray(rows) || rows.length <= 1) return [];

      return rows
        .slice(1)
        .filter((row) => Array.isArray(row) && row.some((cell) => String(cell || '').trim() !== ''))
        .map((row, index) => {
          const options = [];
          for (let i = 10; i < row.length; i++) {
            const value = this.cleanCell(row[i]);
            if (!value) continue;
            options.push({
              key: String.fromCharCode(65 + i - 10),
              value
            });
          }

          return {
            id: index + 1,
            catalog: this.cleanCell(row[0]),
            questionType: this.cleanCell(row[1]),
            bigTitle: this.cleanCell(row[2]),
            subType: this.cleanCell(row[3]),
            title: this.cleanCell(row[4]) || this.cleanCell(row[2]),
            answer: this.cleanCell(row[5]),
            analysis: this.cleanCell(row[6]),
            difficulty: this.cleanCell(row[7]),
            knowledge: this.cleanCell(row[8]),
            options
          };
        });
    },

    cleanCell(value) {
      if (value === null || value === undefined) return '';
      return String(value)
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },

    async downloadMaskedFile(course) {
      try {
        const response = await fetch(`${API_BASE_URL}/exported-courses/${course.id}/masked-file`, {
          headers: this.getAuthHeaders()
        });

        if (!response.ok) {
          throw new Error('下载失败');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${course.name || course.id}_去答案.xls`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('下载去答案文件失败:', error);
        this.questionsError = error.message || '下载去答案文件失败';
      }
    }
  }
};
</script>

<style scoped>
.exported-courses-page {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.page-header h1 {
  margin: 0 0 8px;
  color: #1f2937;
}

.page-header p {
  margin: 0;
  color: #6b7280;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  color: #6b7280;
  font-size: 14px;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.courses-panel,
.questions-panel {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.panel-header h2 {
  margin: 0;
  color: #111827;
}

.panel-subtitle,
.panel-meta {
  color: #6b7280;
  font-size: 14px;
}

.course-list,
.question-list {
  display: flex;
  gap: 14px;
}

.course-list {
  flex-wrap: wrap;
}

.question-list {
  flex-direction: column;
}

.course-card {
  position: relative;
  overflow: hidden;
  flex: 1 1 260px;
  min-width: 260px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #f3f4f6;
  padding: 0;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
}

.course-card:hover,
.course-card.active {
  border-color: #60a5fa;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.12);
  transform: translateY(-1px);
}

.course-cover {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.course-cover-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #f3f4f6;
  background-image: linear-gradient(135deg, #e5e7eb, #d1d5db);
}

.course-cover-placeholder-icon {
  width: 72px;
  height: 72px;
  color: #9ca3af;
  fill: currentColor;
}

.course-cover-placeholder-text {
  color: #6b7280;
  font-size: 14px;
  line-height: 1;
}

.course-info {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 40px 14px 14px;
  background: linear-gradient(180deg, rgba(17, 24, 39, 0), rgba(17, 24, 39, 0.82));
}

.course-info h3 {
  margin: 0;
  color: #fff;
  font-size: 16px;
  line-height: 1.4;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
}

.question-card {
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 16px;
  background: #fafafa;
}

.question-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.question-index {
  font-size: 13px;
  color: #6b7280;
}

.question-tag {
  padding: 4px 10px;
  border-radius: 999px;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 12px;
}

.question-title {
  margin: 0 0 10px;
  color: #111827;
  line-height: 1.7;
  font-size: 16px;
}

.question-extra,
.analysis-text {
  margin: 8px 0 0;
  color: #4b5563;
  line-height: 1.6;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.option-item {
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
}

.option-key {
  width: 20px;
  color: #2563eb;
  font-weight: 600;
  flex-shrink: 0;
}

.option-value {
  color: #374151;
  line-height: 1.6;
}

.question-footer {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 14px;
  color: #1f2937;
}

.question-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
}

.load-more-indicator,
.load-more-finished,
.load-more-error {
  margin-top: 18px;
  text-align: center;
}

.load-more-indicator,
.load-more-finished {
  color: #6b7280;
}

.load-more-indicator {
  padding: 12px 0 4px;
}

.load-more-indicator p,
.load-more-finished,
.load-more-error {
  margin: 0;
}

.load-more-error {
  color: #b91c1c;
}

.page-btn,
.refresh-btn,
.download-btn {
  border: none;
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.link-btn {
  border: none;
  background: transparent;
  padding: 0;
  color: #2563eb;
  cursor: pointer;
  font-size: 14px;
}

.refresh-btn,
.download-btn {
  background: linear-gradient(135deg, #2563eb, #3b82f6);
  color: #fff;
}

.page-btn {
  background: #eef2ff;
  color: #3730a3;
}

.page-btn:disabled,
.refresh-btn:disabled,
.download-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.error-message,
.empty-state,
.loading-state {
  border-radius: 14px;
  padding: 24px 18px;
  text-align: center;
}

.error-message {
  background: #fef2f2;
  color: #b91c1c;
}

.empty-state {
  background: #f9fafb;
  color: #6b7280;
}

.loading-state {
  background: #f8fafc;
  color: #64748b;
}

.spinner {
  width: 34px;
  height: 34px;
  margin: 0 auto 12px;
  border: 3px solid rgba(59, 130, 246, 0.18);
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .header-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .exported-courses-page {
    padding: 10px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .courses-panel,
  .questions-panel {
    padding: 16px;
    border-radius: 14px;
  }

  .panel-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .course-card {
    flex-basis: 100%;
    min-width: 0;
  }

  .course-cover {
    aspect-ratio: 16 / 9;
  }

  .question-footer {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
